import { Helmet } from 'react-helmet';
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Download, Building2, Users, UserCheck, FileText, FileSpreadsheet, Loader2 } from 'lucide-react';
import { exportReportData, getReportsSummary } from '../../lib/apiService';
import * as XLSX from 'xlsx-js-style';

const ReportsPage = () => {
  const { userProfile } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});

  useEffect(() => {
    if (!userProfile) return;
    getReportsSummary()
      .then(res => setSummary(res?.summary || null))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [userProfile]);

  const handleExport = async (type) => {
    setDownloading(prev => ({ ...prev, [type]: true }));
    try {
      const res = await exportReportData(type);
      if (!res?.status || !res?.data) {
        throw new Error(res?.message || 'No data found for export');
      }

      const worksheet = XLSX.utils.json_to_sheet(res.data);

      // Post-process cells to properly render hyperlinks and style headers
      for (const cellAddress in worksheet) {
        if (cellAddress.startsWith('!')) continue;
        const cell = worksheet[cellAddress];

        // Render hyperlinks
        if (typeof cell.v === 'string' && cell.v.startsWith('=HYPERLINK')) {
          const urlMatch = cell.v.match(/=HYPERLINK\("(.*?)",\s*"(.*?)"\)/);
          if (urlMatch && urlMatch[1] && urlMatch[2]) {
            cell.v = urlMatch[2]; // Use the custom text from the formula
            cell.l = { Target: urlMatch[1] };
          }
        }

        // Bold header row (any cell ending in '1')
        if (/^[A-Z]+1$/.test(cellAddress)) {
          cell.s = { font: { bold: true } };
        }
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, type);

      // Generate buffer
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const dataBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });

      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.setAttribute('download', `${type}_${today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} exported successfully`);
    } catch (err) {
      console.error('Export Error:', err);
      toast.error(err.message || `Failed to export ${type}`);
    } finally {
      setDownloading(prev => ({ ...prev, [type]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-[#ee7923] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const STATUS_COLORS = {
    'Flats Available': 'border-blue-200 text-blue-700 bg-transparent',
    'PG Available': 'border-blue-200 text-blue-700 bg-transparent',
    'Apartments Available': 'border-blue-200 text-blue-700 bg-transparent',
    'House Available': 'border-blue-200 text-blue-700 bg-transparent',
    'Properties Booked / Sold / Rented': 'border-emerald-200 text-emerald-700 bg-transparent',
    
    // Lead statuses
    new: 'border-blue-200 text-blue-700 bg-transparent',
    contacted: 'border-yellow-300 text-yellow-700 bg-transparent',
    site_visit: 'border-purple-200 text-purple-700 bg-transparent',
    negotiation: 'border-orange-200 text-orange-700 bg-transparent',
    closed: 'border-emerald-200 text-emerald-700 bg-transparent',
  };

  return (
    <div className="p-6 lg:p-8">
      <Helmet>
        <title>Reports | JoldiPabo Admin</title>
      </Helmet>
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Reports & Export</h1>
        <p className="text-sm text-slate-600 mt-1">Download data and view summary reports</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card className="p-5" data-testid="report-stat-properties">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-orange-50"><Building2 className="w-5 h-5 text-orange-600" /></div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Properties</p>
              <p className="text-2xl font-semibold text-slate-900">{summary?.total_properties || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-5" data-testid="report-stat-users">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-emerald-50"><Users className="w-5 h-5 text-emerald-600" /></div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Users</p>
              <p className="text-2xl font-semibold text-slate-900">{summary?.total_users || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-5" data-testid="report-stat-leads">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-blue-50"><UserCheck className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Callback Requests</p>
              <p className="text-2xl font-semibold text-slate-900">{summary?.total_leads || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-5" data-testid="report-stat-notes">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-amber-50"><FileText className="w-5 h-5 text-amber-600" /></div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Notes</p>
              <p className="text-2xl font-semibold text-slate-900">{summary?.total_notes || 0}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Export Cards */}
      <h2 className="text-lg sm:text-xl font-medium tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Export Data</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        {[
          { type: 'properties', icon: Building2, label: 'Properties', desc: 'All property listings with pricing, location, seller info, and status', count: summary?.total_properties || 0, color: 'orange' },
          { type: 'users', icon: Users, label: 'Users', desc: 'Complete user directory with contact information', count: summary?.total_users || 0, color: 'emerald' },
          { type: 'leads', icon: UserCheck, label: 'Callback Requests', desc: 'All buyer leads with status, assigned agent, and contact details', count: summary?.total_leads || 0, color: 'blue' },
        ].map(item => {
          const Icon = item.icon;
          return (
            <Card key={item.type} className="p-6 flex flex-col justify-between" data-testid={`export-card-${item.type}`}>
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`p-2.5 rounded-lg bg-${item.color}-50`}>
                    <Icon className={`w-5 h-5 text-${item.color}-600`} />
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif', marginBottom: -5 }}>{item.label}</h3>
                    <span className="text-xs text-slate-500">{item.count} records</span>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-5">{item.desc}</p>
              </div>
              <Button
                onClick={() => handleExport(item.type)}
                disabled={downloading[item.type] || item.count === 0}
                className="w-full bg-slate-900 hover:bg-slate-800 disabled:opacity-50"
                data-testid={`export-btn-${item.type}`}
              >
                {downloading[item.type] ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Exporting...</>
                ) : (
                  <><FileSpreadsheet className="w-4 h-4 mr-2" /> Download XLSX</>
                )}
              </Button>
            </Card>
          );
        })}
      </div>

      {/* Status Breakdowns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6" data-testid="property-status-breakdown">
          <h3 className="text-base font-medium text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Property Status Breakdown</h3>
          {summary?.property_status && Object.keys(summary.property_status).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(summary.property_status).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className={`border shadow-none hover:bg-transparent text-xs capitalize ${STATUS_COLORS[status] || 'border-slate-200 bg-transparent text-slate-700'}`}>
                      {status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-32 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#ee7923] transition-all"
                        style={{ width: `${summary.total_properties > 0 ? (count / summary.total_properties) * 100 : 0}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-slate-700 w-8 text-right">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No property data available</p>
          )}
        </Card>

        <Card className="p-6" data-testid="lead-status-breakdown">
          <h3 className="text-base font-medium text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Lead Status Breakdown</h3>
          {summary?.lead_status && Object.keys(summary.lead_status).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(summary.lead_status).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className={`border shadow-none hover:bg-transparent text-xs capitalize ${STATUS_COLORS[status] || 'border-slate-200 bg-transparent text-slate-700'}`}>
                      {status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-32 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-blue-500 transition-all"
                        style={{ width: `${summary.total_leads > 0 ? (count / summary.total_leads) * 100 : 0}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-slate-700 w-8 text-right">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No lead data available</p>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ReportsPage;
